// User admin client (plan §4 /admin/users, spec F18 and J9; owner only through user.manage).
// Its own factory over the shared http layer. Writes carry the version the owner saw (If-Match);
// a create carries an Idempotency-Key, so a retry after a lost answer adds the person once.
import type { Role } from '@cis/shared';
import { createHttp, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  adminUserPageSchema,
  adminUserSchema,
  deactivateResultSchema,
  openWorkSchema,
  revokeResultSchema,
  type AdminUser,
  type AdminUserPage,
  type DeactivateResult,
  type OpenWork,
} from './schemas/users.ts';

export * from './schemas/users.ts';

export interface UserListParams {
  readonly page?: number;
  readonly pageSize?: number;
  /** Part of a name or an email. */
  readonly q?: string | undefined;
  readonly role?: Role | undefined;
  readonly active?: boolean | undefined;
}

export interface NewUser {
  readonly email: string;
  readonly displayName: string;
  readonly role: Role;
  readonly site: string | null;
}

/** The fields the owner changes; only those sent are changed. */
export interface UserChanges {
  readonly email?: string;
  readonly displayName?: string;
  readonly role?: Role;
  readonly site?: string | null;
}

export interface VersionedUser {
  readonly id: number;
  readonly version: number;
}

export interface UsersApi {
  /** GET /admin/users: one page, with search, role and active filters. */
  list(params?: UserListParams, signal?: AbortSignal): Promise<AdminUserPage>;
  /** GET /admin/users/:id */
  get(id: number, signal?: AbortSignal): Promise<AdminUser>;
  /** GET /admin/users/:id/open-work: open tasks and unticked steps the person still holds. */
  openWork(id: number, signal?: AbortSignal): Promise<OpenWork>;
  /** POST /admin/users (201). An attester needs a site (400 on `site` otherwise). */
  create(user: NewUser, idempotencyKey: string): Promise<AdminUser>;
  /** PATCH /admin/users/:id with If-Match. */
  update(user: VersionedUser, changes: UserChanges): Promise<AdminUser>;
  /** POST /admin/users/:id/deactivate with If-Match: ends every session, returns the open work. */
  deactivate(user: VersionedUser): Promise<DeactivateResult>;
  /** POST /admin/users/:id/reactivate with If-Match. */
  reactivate(user: VersionedUser): Promise<AdminUser>;
  /** POST /admin/users/:id/revoke-sessions: signs the person out everywhere. */
  revokeSessions(id: number): Promise<number>;
}

export function userListQuery(params: UserListParams): string {
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
  if (params.q !== undefined && params.q !== '') qs.set('q', params.q);
  if (params.role !== undefined) qs.set('role', params.role);
  if (params.active !== undefined) qs.set('active', String(params.active));
  const text = qs.toString();
  return text === '' ? '' : `?${text}`;
}

const userPath = (id: number, action = ''): string =>
  `/admin/users/${pathSegment(id)}${action === '' ? '' : `/${action}`}`;

export function createUsersApi(options: HttpOptions = {}): UsersApi {
  const http = createHttp(options);
  return {
    list: (params = {}, signal) =>
      http.json(
        { method: 'GET', path: `/admin/users${userListQuery(params)}`, signal },
        adminUserPageSchema,
      ),
    get: (id, signal) => http.json({ method: 'GET', path: userPath(id), signal }, adminUserSchema),
    openWork: (id, signal) =>
      http.json({ method: 'GET', path: userPath(id, 'open-work'), signal }, openWorkSchema),
    create: (user, idempotencyKey) =>
      http.json(
        { method: 'POST', path: '/admin/users', body: user, idempotencyKey },
        adminUserSchema,
      ),
    update: (user, changes) =>
      http.json(
        { method: 'PATCH', path: userPath(user.id), version: user.version, body: changes },
        adminUserSchema,
      ),
    deactivate: (user) =>
      http.json(
        { method: 'POST', path: userPath(user.id, 'deactivate'), version: user.version },
        deactivateResultSchema,
      ),
    reactivate: (user) =>
      http.json(
        { method: 'POST', path: userPath(user.id, 'reactivate'), version: user.version },
        adminUserSchema,
      ),
    revokeSessions: async (id) =>
      (
        await http.json(
          { method: 'POST', path: userPath(id, 'revoke-sessions') },
          revokeResultSchema,
        )
      ).sessionsRevoked,
  };
}
